import {Controller, useFormState, useWatch} from "react-hook-form";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Box, FormHelperText, Grid, InputAdornment, TextField} from "@mui/material";
import {CheckCircle, ErrorOutlined} from "@mui/icons-material";
import {useStyles} from "./uiUtils";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import {isNumberLike, isPlainDict, toBytes} from "./utils";
import {applyNationalInput, DIAL_CODES, joinPhoneNumber, splitPhoneNumber} from "./phoneNumbers";
import clsx from "clsx";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";


/**
 * ValidatedAdornedField component renders a checked text field with optional adornments.
 * Must be used within a form managed by react-hook-form.
 * Generally, it will render a text type field.
 * If rows > 1, it will render a textarea field (multiline) that accepts pressing enter inside it without submitting the form.
 * If selectValueLabelMap is provided, it will render a select field with options.
 * @param {string} name - The name of the field, which should match the name used in the form.
 * @param {string} label - The label for the text field.
 * @param {function} register - The register function from react-hook-form to register the field.
 * @param {object} control - The control object from react-hook-form to manage the field's state.
 * @param {function} trigger - The trigger function from react-hook-form to manually trigger validation. Required only for select fields to trigger validation on change.
 * @param {object} classes - The classes object for styling the field.
 * @param {object} rules - Validation rules for the field, passed to react-hook-form.
 * @param {boolean} endAdornmentEnabled - Whether to show the end adornment (check or error icon).
 * @param {boolean} disabledStatus - Whether the field should be disabled.
 * @param {number} xs - The grid size for the field, defaults to 6.
 * @param {number} rows - The number of rows for multiline text fields, defaults to 1.
 * @param {Array} selectValueLabelMap - An array of objects with value and label for select fields, if applicable.
 * @returns {Element}
 * @constructor
 */
export const ValidatedAdornedField = ({
                                          name,
                                          label,
                                          register,
                                          control,
                                          trigger = undefined,
                                          classes,
                                          rules = {},
                                          endAdornmentEnabled = true,
                                          disabledStatus = false,
                                          xs = 6,
                                          rows = 1,
                                          selectValueLabelMap = undefined
                                      }) => {
    const value = useWatch({control, name});
    const {errors} = useFormState({control, name}); // with main form's mode and reValidateMode "onChange", this component will be re-rendered on every change
    const hasError = Boolean(errors[name]); // re-rendered on every change of errors will fire the calculation of hasError at every re-rendering
    const [isMultiline, setIsMultiline] = useState(rows > 1);
    const isRequired = !!rules.required;
    const [isSelect, setIsSelect] = useState(Array.isArray(selectValueLabelMap) && selectValueLabelMap.length > 0);

    const [contextDependentUpdatedRules, setContextDependentUpdatedRules] = useState(rules);

    useEffect(() => {
        setIsMultiline(rows > 1);
    }, [rows]);

    useEffect(() => {
        setIsSelect(Array.isArray(selectValueLabelMap) && selectValueLabelMap.length > 0);
    }, [selectValueLabelMap]);

    useEffect(() => {
        if (isSelect && trigger === undefined) {
            throw new Error("ValidatedAdornedField with selectValueLabelMap must have trigger function passed to it to validate on change");
        }
    }, [isSelect]);

    const inputProps = useCallback(() => {
            return endAdornmentEnabled ? ({
                endAdornment:
                    value && (
                        <InputAdornment position="end">
                            {hasError ?
                                <ErrorOutlined sx={{color: "#FFC540"}}/> :
                                <CheckCircle sx={{color: "#009621"}}/>}
                        </InputAdornment>
                    ),
            }) : ({});
        },
        [value, hasError, endAdornmentEnabled]);

    // the rules will be updated to include onChange handler only if the field is a select field
    useEffect(() => {
        setContextDependentUpdatedRules(!isSelect ? rules : {...rules, onChange: () => trigger(name)})
    }, [isSelect, rules, trigger, name]);

    return (
        <Grid item xs={xs}>
            <TextField
                label={label}
                required={isRequired}
                {...register(name, contextDependentUpdatedRules)}
                error={hasError}
                InputProps={inputProps}
                className={classes.selectLabel}
                fullWidth
                disabled={disabledStatus}
                multiline={isMultiline}
                minRows={isMultiline ? rows : undefined}
                type={isMultiline || isSelect ? undefined : "text"}
                select={isSelect}
                helperText={errors[name]?.message}
                sx={{
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {borderColor: "#FFC540"},
                    // Asterisk takes the label's own colour (grey when idle) rather than standing out in red.
                    "& .MuiInputLabel-asterisk": {color: "inherit"},
                    "& .MuiFormLabel-root.Mui-error": {color: "#FFC540"},
                    "& .MuiFormHelperText-root.Mui-error": {color: "#FFC540"},
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input": {WebkitTextFillColor: "rgba(255,255,255,0.5)"},
                    "& .MuiOutlinedInput-root.Mui-disabled textarea": {WebkitTextFillColor: "rgba(255,255,255,0.5)"},
                }}
            >
                {isSelect ?
                    selectValueLabelMap.map((option) => [
                        <MenuItem key={`${name} select option-${option.value}`} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ]) :
                    <></>}
            </TextField>
        </Grid>
    );
}

/**
 * ValidatedDialledNumberField renders a phone number as two controls: the country code picked from a list,
 * the national digits typed in the box beside it. Must be used within a form managed by react-hook-form.
 *
 * The form field holds the joined string ("+61 421 127 053") — the shape the API stores and every caller
 * already reads — so nothing downstream knows the field was ever split. This is the third place in the
 * portal to need the pair (the Add/Edit User dialog's dialledNumberInput and the Account step's Primary
 * Contact are the other two), which is why it lives here rather than being copied a third time.
 *
 * @param {string} name - The name of the field, which should match the name used in the form.
 * @param {string} label - The label for the number box. The dropdown is always labelled "Country code".
 * @param {object} control - The control object from react-hook-form to manage the field's state.
 * @param {object} classes - The classes object for styling the field, as for ValidatedAdornedField.
 * @param {object} rules - Validation rules. A pattern is checked against the national half only — see below.
 * @param {boolean} disabledStatus - Whether both controls should be disabled.
 * @param {number} xs - The grid size for the pair, defaults to 6.
 * @param {string} dataTestId - Testid for the wrapper; the dropdown carries `${dataTestId}-code`.
 * @returns {Element}
 * @constructor
 */
export const ValidatedDialledNumberField = ({
                                                name,
                                                label,
                                                control,
                                                classes,
                                                rules = {},
                                                disabledStatus = false,
                                                xs = 6,
                                                dataTestId = undefined,
                                            }) => {
    // The code the operator picked, which only stands in while the number box is empty and the stored string
    // therefore has no code of its own to read back.
    const [pickedDialCode, setPickedDialCode] = useState(undefined);
    const isRequired = !!rules.required;

    /*
     * The configured pattern describes the national number, but the value held by the form is the joined
     * string, so a pattern rule would test it with the country code still attached and reject everything.
     * Moved to a validate entry that checks the national half. required is left alone: it fires on the empty
     * string, and joinPhoneNumber returns exactly that when the number box is empty, code or no code.
     */
    const {pattern, validate, ...restOfRules} = rules;
    const dialledRules = {
        ...restOfRules,
        validate: {
            ...(isPlainDict(validate) ? validate : (validate ? {configured: validate} : {})),
            ...(pattern ? {
                nationalNumberFormat: (value) => {
                    const national = splitPhoneNumber(value).national;
                    return national === '' || pattern.value.test(national) || pattern.message;
                },
            } : {}),
        },
    };

    const goldErrorSx = {
        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {borderColor: "#FFC540"},
        // Asterisk takes the label's own colour (grey when idle) rather than standing out in red.
        "& .MuiInputLabel-asterisk": {color: "inherit"},
        "& .MuiFormLabel-root.Mui-error": {color: "#FFC540"},
        "& .MuiFormHelperText-root.Mui-error": {color: "#FFC540"},
    };

    return (
        <Grid item xs={xs} data-testid={dataTestId}>
            <Controller
                name={name}
                control={control}
                rules={dialledRules}
                render={({field, fieldState}) => {
                    const stored = splitPhoneNumber(field.value);
                    const dial = stored.national === '' ? (pickedDialCode ?? stored.dial) : stored.dial;

                    const commit = (nextDial, national) => {
                        setPickedDialCode(nextDial);
                        field.onChange(joinPhoneNumber(nextDial, national));
                    };

                    return (
                        <Box sx={{display: "flex", gap: 1}}>
                            <TextField
                                // "Country code", not "Mobile Number code": what the box holds is the country,
                                // and the field it belongs to is already named by the box beside it.
                                label="Country code"
                                data-testid={dataTestId ? `${dataTestId}-code` : undefined}
                                select
                                value={dial}
                                onChange={(e) => commit(e.target.value, stored.national)}
                                className={classes.selectLabel}
                                InputLabelProps={{shrink: true}}
                                // Closed, the box shows just "+61" — the country name is only useful while
                                // choosing, and would not fit here anyway.
                                SelectProps={{renderValue: (selected) => selected}}
                                sx={{flex: "0 0 auto", width: 124, ...goldErrorSx}}
                                disabled={disabledStatus}
                            >
                                {/* Country first, matching the rest of the portal: the list is every dialling
                                  * code in use, sorted by country, and long enough that a leading code reads
                                  * as unsorted. MUI's type-ahead matches the start of the text too. */}
                                {DIAL_CODES.map(({dial: dialOption, label: country}) => (
                                    <MenuItem key={dialOption} value={dialOption}>
                                        {`${country}  ${dialOption}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                {...field}
                                label={label}
                                required={isRequired}
                                inputProps={{maxLength: 300, inputMode: "tel"}}
                                // The box holds the national half; the code lives in the select beside it.
                                value={stored.national}
                                // A whole international number pasted in here ("+65 9123 4567" out of an
                                // email) moves its code into the select instead of being kept as digits.
                                onChange={(e) => {
                                    const applied = applyNationalInput(e.target.value, dial);
                                    commit(applied.dial, applied.national);
                                }}
                                className={classes.selectLabel}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                fullWidth
                                disabled={disabledStatus}
                                sx={goldErrorSx}
                            />
                        </Box>
                    );
                }}
            />
        </Grid>
    );
}

export const getFilterDefault = (filterStructure) => {
    const filterType = filterStructure.Type;
    const filterOptions = filterStructure.Options || {};

    switch (filterType) {
        case "toggleInput":
            return {defaultValue: filterOptions.defaultValue ?? false}
        case "dateInput":
            return {defaultValue: ''};
        case "multiselect":
            return {defaultValue: []};
        case "sizeInput":
            return {defaultValue: "", sizeUnitValue: 'B'};
        default:                       // text / int / float
            return {defaultValue: ""};
    }
};

/**
 * FilterField component is a placeholder for rendering filter fields based on the provided filter structure. It will return one ore more Grid items that will occupy 12 columns in the grid.
 * @param filterStructure - A dict like structure that defines the filter field properties. It contains properties like CategoryName, Label, FilterName, Type, Options, etc.
 * @param getOtherFilterDataFn - A function that retrieves other filter data based on the filter name. It is used to get additional data for the filter field.
 * @param control - The control object from react-hook-form to manage the field's state.
 * @param trigger - The trigger function from react-hook-form to manually trigger validation. This is optional and can be undefined.
 * @param errors - The errors object from react-hook-form to display validation errors.
 * @param getValuesFn - A function to get the current values of the form fields. It is used to validate the size input field against dependent fields.
 * @param processedSupportData - A dict like structure that store any options for the multiselect dropdown fields
 * @returns {Element}
 */
export const FilterField = ({
                                filterStructure,
                                getOtherFilterDataFn,
                                control,
                                trigger = undefined,
                                errors,
                                getValuesFn,
                                processedSupportData
                            }) => {

    const {defaultValue, defaultSizeUnitValue = '', fieldProps, rules} = useMemo(() => {
        const cfg = {
            toggleInput: {
                defaultValue: getFilterDefault(filterStructure).defaultValue
            },
            text: {
                defaultValue: getFilterDefault(filterStructure).defaultValue,
                fieldProps: {
                    type: 'text',
                    inputProps: {maxLength: 300, inputMode: 'text'},
                },
                rules: {
                    maxLength: {value: 300, message: 'Maximum 300 characters'},
                },
            },
            int: {
                defaultValue: getFilterDefault(filterStructure).defaultValue,
                fieldProps: {
                    type: 'number',
                    inputProps: {step: 1, pattern: '[0-9]*', inputMode: 'numeric'},
                },
                rules: {
                    valueAsNumber: true,
                    pattern: {value: /^\d+$/, message: 'Whole numbers only'},
                },
            },
            sizeInput: {
                defaultValue: getFilterDefault(filterStructure).defaultValue,
                defaultSizeUnitValue: getFilterDefault(filterStructure).sizeUnitValue,
                fieldProps: {
                    type: 'number',
                    inputProps: {step: 0.01, min: 0, max: 99999999.99, inputMode: 'decimal'},
                },
                rules: {
                    valueAsNumber: true,
                    pattern: {
                        value: /^\d+([.,]\d+)?$/,
                        message: 'Decimal number (e.g. 123.45)',
                    },
                },
            },
            dateInput: {
                defaultValue: getFilterDefault(filterStructure).defaultValue,
                fieldProps: {
                    type: 'date',
                    InputLabelProps: {shrink: true},
                },
                rules: {},          // add any custom date checks here
            },
            multiselect: {
                defaultValue: getFilterDefault(filterStructure).defaultValue,
                fieldProps: {type: undefined}
            }
            // multiselect will normally use an <Autocomplete multiple> or <Select multiple>,
            // so it isn’t handled by TextField directly.
        }
        return cfg[filterStructure.Type] ?? {defaultValue: ""};
    }, [filterStructure]);

    const classes = useStyles();
    const [isMultiSelect, setIsMultiSelect] = useState(false);

    useEffect(() => {
        if (filterStructure.Type === 'multiselect') setIsMultiSelect(true);
        else setIsMultiSelect(false);
    }, [filterStructure]);


    const [dependsOnFieldName, setDependsOnFieldName] = useState(undefined);
    const [dependencyType, setDependencyType] = useState(undefined);
    const dependentFieldValue = useWatch({
        control,
        name: dependsOnFieldName,
        defaultValue: undefined,
        disabled: !dependsOnFieldName
    });

    useEffect(() => {
        const validationDependency = filterStructure.Options?.EntryValidation?.Dependency;
        setDependsOnFieldName(validationDependency || undefined);
        setDependencyType(filterStructure.Options?.EntryValidation?.DependencyType || undefined);
    }, [filterStructure]);

    const errorColorFix = {
        FormHelperTextProps: {
            sx: {fontWeight: 500}, // weight only, color overridden below
        },
        sx: {
            // helper text (error message)
            "& .MuiFormHelperText-root.Mui-error": {
                color: "#FFC540",
                fontWeight: 500,
            },
            // outline/border when in error
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFC540",
            },
            // label when in error
            "& .MuiFormLabel-root.Mui-error": {
                color: "#FFC540",
            },
        },
    };


    const renderTextField = useCallback(() => {
        if (!['text'].includes(filterStructure.Type)) return <></>;

        return (
            <Grid item xs={12}>
                <Controller
                    name={filterStructure.FilterName}
                    control={control}
                    defaultValue={defaultValue}
                    rules={rules}
                    render={({field, fieldState}) => (
                        <TextField
                            {...field}
                            {...fieldProps}
                            {...errorColorFix}
                            className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                            label={filterStructure?.Label || 'Unknown Filter Label'}
                            error={!!fieldState.error}
                            fullWidth
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </Grid>);
    }, [filterStructure, control, errors, defaultValue, rules, fieldProps, classes]);

    const renderMultiSelectField = useCallback(() => {
        // Ex:
        // {
        //         CategoryName: "Type & Size",
        //         Label: "File type",
        //         FilterName: "type_ids",
        //
        //         Type: "multiselect",
        //         Options: {
        //             EntrySource: 'file_types',
        //             EntryDisplay: "mnemonic",
        //             EntryValue: "id",
        //             EntryValidation: {
        //                 // this filter option will be valid only if the value of LocalProperty (Eg. 'file_group_ids') will be in the ValidationDependency values. (Eg. field 'group_ids' -> property 'id')
        //                 // if 'group_ids' is not selected, means All and this filter is considered valid
        //                 Dependency: "group_ids",
        //                 DependencyType: "in_larger_category",
        //                 LocalProperty: "file_group_ids",
        //                 DependencyProperty: "id"
        //             }
        //         }
        //     }

        if (!['multiselect'].includes(filterStructure.Type)) return <></>;

        const currentFilterOptionsSource = filterStructure.Options?.EntrySource;
        let menuItemList = [];

        // Getting the entries for each MenuItem either:
        // 1. from processedSupportData
        //    where processedSupportData[key] and filter structure -> Options -> EntrySource = key
        // 2. either directly from filter structure -> Options -> EntrySource
        //    where filter structure -> Options -> EntrySource is a list of objects
        if (typeof currentFilterOptionsSource === 'string') menuItemList = [...(processedSupportData?.[currentFilterOptionsSource] ?? [])];
        else if (Array.isArray(currentFilterOptionsSource)) menuItemList = [...currentFilterOptionsSource];

        // --------------------- Validation of each menu item  ---------------------
        // This might occur if for example current field is a file type filter and another filter like file type group, already filtered only database type files. So this one, should include only database type files.
        let filteredMenuItemList = [];

        if (!dependentFieldValue || Array.isArray(dependentFieldValue) && dependentFieldValue.length === 0) {
            // no dependent filters were set
            filteredMenuItemList = [...menuItemList];
        } else {
            const dependentFieldStructure = getOtherFilterDataFn(dependsOnFieldName);

            if (!dependentFieldStructure) filteredMenuItemList = [...menuItemList] // at first render the dependent Field Structure is not loaded or later, no filter will be selected and thus dependentFieldValue will be an object
            else {
                if (!Array.isArray(dependentFieldValue)) filteredMenuItemList = [...menuItemList]; // if the field of which this one is dependent of, has no value selected, watch will return all entries. Otherwise, if one or more values are selected, watch will return an array of values. The else branch means there is a selection in the field of which this one is dependent of
                else {
                    if (!['multiselect'].includes(dependentFieldStructure.Type)) throw new Error(`Unsupported dependent field type: ${dependentFieldStructure.Type} for filter: ${filterStructure?.FilterName}`);
                    if (!['in_larger_category'].includes(dependencyType)) throw new Error(`Unsupported dependency type: ${dependencyType} for filter: ${filterStructure?.FilterName}`);

                    const validationSet = new Set();
                    const dependentFieldPropertyNameForValue = dependentFieldStructure?.Options?.EntryValue;
                    const dependentFilterOptionsSource = dependentFieldStructure?.Options?.EntrySource;
                    // dependent field is the other field that influences the values of the current one.
                    // Property Name is the name of the property that is actually used as value. Ex: id.
                    // All the values of that field represent this property name (ex: id)
                    // Options Source is the actual dict with options or the name of the property containing these options

                    let dependentFieldAllEntries = [];
                    if (typeof dependentFilterOptionsSource === 'string') dependentFieldAllEntries = [...(processedSupportData?.[dependentFilterOptionsSource] || [])];
                    else if (Array.isArray(dependentFilterOptionsSource)) dependentFieldAllEntries = [...dependentFilterOptionsSource];
                    // dependentFieldAllEntries will contain all the possible options of the other field that influences the current one

                    dependentFieldAllEntries.forEach(entry => {
                        const valueCandidate = entry[dependentFieldPropertyNameForValue]; // if the other field has one or more values selected, this might be in the array of selected values
                        if (dependentFieldValue.includes(valueCandidate)) {
                            // this candidate option is selected

                            const propertyToExtractFromCurrentOption = filterStructure?.Options?.EntryValidation?.DependencyProperty;
                            const valueUsedForValidation = entry[propertyToExtractFromCurrentOption];
                            validationSet.add(valueUsedForValidation)
                        }
                    });

                    const localPropertyNameContainingValidationValue = filterStructure?.Options?.EntryValidation?.LocalProperty; // the property name in current field options, in which will will be stored a value referencing the depending option.
                    //Ex: if the other field validation values may refer entry ids, in current field under "parent_field_id" we find the reference to one of those ids. Entry x might have "parent_field_id" = 3 meaning it is linked to the entry with id 3 from the other field

                    filteredMenuItemList = menuItemList.filter(currentFilterOption => {
                        const reference_s_ToOtherField = currentFilterOption[localPropertyNameContainingValidationValue];

                        if (!Array.isArray(reference_s_ToOtherField)) {
                            // It is not a list of references, so it is a single reference
                            return validationSet.has(reference_s_ToOtherField);
                        } else {
                            // It is a list of references, so if one of them is in the validation set, it is valid
                            return reference_s_ToOtherField.some(val => validationSet.has(val));
                        }
                    });
                }
            }
        }

        const formControlErrorColorFix = {
            // helper text (error message)
            "& .MuiFormHelperText-root.Mui-error": {
                color: "#FFC540",
                fontWeight: 500,
            },
            // outline/border when in error
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFC540",
            },
            // label when in error
            "& .MuiFormLabel-root.Mui-error": {
                color: "#FFC540",
            },
        };

        const key = filterStructure.Options?.EntryDisplay;
        filteredMenuItemList.sort((a, b) => {
            const valA = a[key] ?? ""; // fallback to empty string
            const valB = b[key] ?? "";
            return String(valA).localeCompare(String(valB));
        });

        return (
            <Grid item xs={12}>
                <Controller
                    name={filterStructure.FilterName}
                    control={control}
                    defaultValue={[]}
                    rules={{
                        ...rules,
                        onChange: () => trigger(filterStructure.FilterName)
                    }}
                    render={({field, fieldState}) => {
                        const value = Array.isArray(field.value) ? field.value : [];

                        const handleChange = (event) => {
                            const {value} = event.target;
                            field.onChange(Array.isArray(value) ? value : [value]);
                            trigger?.(filterStructure.FilterName);
                        };

                        return (
                            <FormControl
                                fullWidth
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                error={!!fieldState.error}
                                sx={formControlErrorColorFix}
                            >
                                <InputLabel id={`${field.name}-label`}>
                                    {filterStructure?.Label || "Unknown Filter Label"}
                                </InputLabel>

                                <Select
                                    {...field} // RHF wiring
                                    labelId={`${field.name}-label`}
                                    multiple={true}
                                    value={value}
                                    onChange={handleChange}
                                    input={
                                        <OutlinedInput
                                            label={filterStructure?.Label || "Unknown Filter Label"}
                                            {...fieldProps} // any extra props you inject
                                        />
                                    }
                                >
                                    {filteredMenuItemList.map((option, idx) => [
                                            <MenuItem
                                                key={`${field.name}_item_${idx}`}
                                                value={option[filterStructure.Options?.EntryValue]}
                                            >
                                                {option[filterStructure.Options?.EntryDisplay]}
                                            </MenuItem>
                                        ]
                                    )}
                                </Select>

                                {fieldState.error?.message && (
                                    <FormHelperText>
                                        {fieldState.error?.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    }}
                />
            </Grid>);
    }, [filterStructure, control, errors, defaultValue, rules, fieldProps, classes, isMultiSelect, processedSupportData, getOtherFilterDataFn, dependsOnFieldName, dependentFieldValue, dependencyType]);

    const renderSizeInputField = useCallback(() => {
        if (!['sizeInput'].includes(filterStructure.Type)) return <></>;
        const filterName = filterStructure.FilterName;

        if (dependencyType && !['upper_limit', 'lower_limit'].includes(dependencyType)) throw new Error(`Unsupported dependency type: ${dependencyType} for filter: ${filterStructure?.FilterName}`);

        const validateSize = (val) => {
            if (val === '' || val === undefined || val === null) return true;
            if (isNaN(val)) return 'Size must be a number';
            if (!dependencyType) return true;

            const values = getValuesFn();
            const curUnit = values[`${filterName}_size_unit`];
            const depVal = values[dependsOnFieldName];
            const depUnit = values[`${dependsOnFieldName}_size_unit`];

            if (depVal === '' || depVal === undefined || depVal === null) return true;
            if (isPlainDict(depVal)) return true;          // ← keep if you really expect dicts

            if (!isNumberLike(depVal)) return 'Dependent field value must be a number';

            const curBytes = toBytes(val, curUnit);
            const depBytes = toBytes(depVal, depUnit);

            if (dependencyType === 'upper_limit' && curBytes > depBytes) {
                return `Size cannot be greater than ${depVal} ${depUnit}.`;
            }
            if (dependencyType === 'lower_limit' && curBytes < depBytes) {
                return `Size cannot be less than ${depVal} ${depUnit}.`;
            }
            return true;
        };

        const enrichedRules = dependencyType
            ? {...rules, validate: validateSize}
            : rules;

        const units = ['B', 'KB', 'MB', 'GB', 'TB'];

        return (
            <>
                <Grid item xs={9}>
                    <Controller
                        name={filterName}
                        control={control}
                        defaultValue={defaultValue}
                        rules={enrichedRules}
                        render={({field, fieldState}) => (
                            <TextField
                                {...field}
                                {...fieldProps}
                                {...errorColorFix}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                label={filterStructure?.Label || 'Unknown Filter Label'}
                                error={!!fieldState.error}
                                fullWidth
                                helperText={fieldState.error?.message}
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([filterName, dependsOnFieldName]);
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Controller
                        name={`${filterName}_size_unit`}
                        control={control}
                        defaultValue={defaultSizeUnitValue}
                        render={({field}) => (
                            <TextField
                                {...field}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                fullWidth
                                select
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([filterName, dependsOnFieldName]);
                                }}
                            >
                                {units.map((sizeUnit, index) => [
                                    <MenuItem
                                        key={`${filterName}_size_unit_${index}`}
                                        value={sizeUnit}
                                    >
                                        {sizeUnit}
                                    </MenuItem>
                                ])}
                            </TextField>
                        )}
                    />
                </Grid>
            </>);
    }, [filterStructure, control, errors, defaultValue, rules, fieldProps, defaultSizeUnitValue, classes, dependsOnFieldName, dependencyType, getValuesFn]);

    const renderDateInputField = useCallback(() => {
        if (!['dateInput'].includes(filterStructure.Type)) return <></>;
        const filterName = filterStructure.FilterName;

        if (dependencyType && !['upper_limit', 'lower_limit'].includes(dependencyType)) throw new Error(`Unsupported dependency type: ${dependencyType} for filter: ${filterStructure?.FilterName}`);

        const validateDate = (val) => {
            const values = getValuesFn();
            const depVal = values[dependsOnFieldName];

            if (!val || val === '' || val === undefined || val === null) return true;
            if (!dependencyType) return true;

            if (!depVal || depVal === '' || depVal === undefined || depVal === null) return true;
            if (isPlainDict(depVal)) return true;          // ← keep if you really expect dicts

            const curDate = new Date(val);
            const depDate = new Date(depVal);

            if (dependencyType === 'upper_limit' && curDate > depDate) {
                return `Date cannot be later than ${depVal}.`;
            }
            if (dependencyType === 'lower_limit' && curDate < depDate) {
                return `Size cannot be earlier than ${depVal}.`;
            }
            return true;
        };

        const enrichedRules = dependencyType
            ? {...rules, validate: validateDate}
            : rules;

        return (
            <>
                <Grid item xs={12}>
                    <Controller
                        name={filterName}
                        control={control}
                        defaultValue={defaultValue}
                        rules={enrichedRules}
                        render={({field, fieldState}) => (
                            <TextField
                                {...field}
                                {...fieldProps}
                                {...errorColorFix}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                label={filterStructure?.Label || 'Unknown Filter Label'}
                                error={!!fieldState.error}
                                fullWidth
                                helperText={fieldState.error?.message}
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([filterName, dependsOnFieldName]);
                                }}
                            />
                        )}
                    />
                </Grid>
            </>);
    }, [filterStructure, control, errors, defaultValue, rules, fieldProps, defaultSizeUnitValue, classes, dependsOnFieldName, dependencyType, getValuesFn]);

    const renderToggleInputField = useCallback(() => {
        if (!['toggleInput'].includes(filterStructure.Type)) return <></>;
        const filterName = filterStructure.FilterName;

        return (
            <Grid item xs={12}>
                <Controller
                    name={filterName}
                    control={control}
                    defaultValue={defaultValue}
                    render={({field, fieldState}) => (
                        <FormControl
                            error={!!fieldState.error}
                            component="fieldset"
                            variant="standard"
                            className={clsx(classes.switchLabelWhite, classes.switchLabelGreyDiff)}
                        >
                            <FormControlLabel
                                label={filterStructure.Label || 'Unknown Filter Label'}
                                control={
                                    <Switch
                                        inputRef={field.ref}
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        onBlur={field.onBlur}
                                    />
                                }
                            />
                            {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
            </Grid>
        );

    }, [filterStructure, control, errors, defaultValue, rules, fieldProps, defaultSizeUnitValue, classes, dependsOnFieldName, dependencyType, getValuesFn]);

    const unknownTypeFieldRenderer = () => {
        console.log(`Unknown filter type: ${filterStructure?.Type} for filter: ${filterStructure?.FilterName}. Entire structure ->`, filterStructure);
        return <></>
    };

    const textFieldRendererMap = () => {
        return renderTextField();
    };

    const multiselectFieldRenderer = () => {
        return renderMultiSelectField();
    };

    const sizeInputFieldRenderer = () => {
        return renderSizeInputField();
    };

    const dateInputFieldRenderer = () => {
        return renderDateInputField();
    };

    const toggleInputFieldRenderer = () => {
        return renderToggleInputField()
    };

    const fieldRendererMap = {
        defValue: unknownTypeFieldRenderer,
        text: textFieldRendererMap,
        multiselect: multiselectFieldRenderer,
        sizeInput: sizeInputFieldRenderer,
        dateInput: dateInputFieldRenderer,
        toggleInput: toggleInputFieldRenderer,
    };

    return (fieldRendererMap[filterStructure?.Type] || fieldRendererMap.defValue)();
};